import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [favIds, setFavIds] = useState(new Set()); // Set of "kind:item_id"

  const refresh = useCallback(async () => {
    if (!user) { setFavIds(new Set()); return; }
    try {
      const { data } = await api.get("/favorites/ids");
      setFavIds(new Set(data.map((f) => `${f.kind}:${f.item_id}`)));
    } catch (e) {
      setFavIds(new Set());
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isFav = (kind, item_id) => favIds.has(`${kind}:${item_id}`);

  const toggle = async (kind, item_id) => {
    if (!user) return { needLogin: true };
    const key = `${kind}:${item_id}`;
    if (favIds.has(key)) {
      await api.delete(`/favorites/${item_id}`, { params: { kind } });
      const next = new Set(favIds); next.delete(key); setFavIds(next);
      return { added: false };
    } else {
      await api.post("/favorites", { item_id, kind });
      const next = new Set(favIds); next.add(key); setFavIds(next);
      return { added: true };
    }
  };

  return (
    <FavoritesContext.Provider value={{ isFav, toggle, refresh, count: favIds.size }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export const useFavorites = () => useContext(FavoritesContext);
